import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Navigation from '../components/Navigation';
import { BookOpen, Heart, Users, Sparkles, Calendar, TrendingUp, MessageCircle, Trophy, Book, Youtube, Edit3, DollarSign, Sun, Activity, Share2, Globe, Quote, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AlphaVideoModal } from '../components/AlphaVideoModal';
import IntroVideoPopup from '../components/IntroVideoPopup';
import DashboardGroupsCard from '../components/DashboardGroupsCard';

const Dashboard = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [dailyVerse, setDailyVerse] = useState(null);

  const fetchDailyVerse = useCallback(async () => {
    try {
      // Try to get verse from our backend
      const response = await axios.get(`${API}/daily-verse`);
      setDailyVerse(response.data);
    } catch (error) {
      // Fallback verses if API fails
      const fallbackVerses = [
        { text: "For I know the plans I have for you, declares the LORD, plans for welfare and not for evil, to give you a future and a hope.", reference: "Jeremiah 29:11" },
        { text: "Trust in the LORD with all your heart, and do not lean on your own understanding.", reference: "Proverbs 3:5" },
        { text: "I can do all things through him who strengthens me.", reference: "Philippians 4:13" },
        { text: "The LORD is my shepherd; I shall not want.", reference: "Psalm 23:1" },
        { text: "Be strong and courageous. Do not be frightened, and do not be dismayed, for the LORD your God is with you wherever you go.", reference: "Joshua 1:9" },
        { text: "And we know that for those who love God all things work together for good, for those who are called according to his purpose.", reference: "Romans 8:28" },
        { text: "But they who wait for the LORD shall renew their strength; they shall mount up with wings like eagles; they shall run and not be weary; they shall walk and not faint.", reference: "Isaiah 40:31" }
      ];
      // Use day of year to pick a consistent verse for the day
      const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
      setDailyVerse(fallbackVerses[dayOfYear % fallbackVerses.length]);
    }
  }, [API]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, entriesRes] = await Promise.all([
        axios.get(`${API}/stats/summary`),
        axios.get(`${API}/journal/recent?limit=5`)
      ]);
      
      setStats(statsRes.data);
      setRecentEntries(entriesRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchDashboardData();
    fetchDailyVerse();
  }, [fetchDashboardData, fetchDailyVerse]);

  const vitalSigns = [
    {
      key: 'pursue_god',
      title: 'Pursue God',
      icon: Heart,
      color: '#2B6B7F',
      bgColor: 'bg-[#2B6B7F]/10',
      description: 'Prayer & Communication'
    },
    {
      key: 'engage_scripture',
      title: 'Engage Scripture',
      icon: BookOpen,
      color: '#5FA8C1',
      bgColor: 'bg-[#5FA8C1]/10',
      description: 'Bible Study & Reflection'
    },
    {
      key: 'serve_others',
      title: 'Serve Others',
      icon: Users,
      color: '#7BA888',
      bgColor: 'bg-[#7BA888]/10',
      description: 'Acts of Service'
    },
    {
      key: 'radiate_jesus',
      title: 'Radiate Jesus',
      icon: Sparkles,
      color: '#D4A574',
      bgColor: 'bg-[#D4A574]/10',
      description: 'Sharing the Gospel'
    }
  ];

  const stageInfo = {
    explore: { label: 'Explore - Connect', description: 'Experience the Living Water' },
    engage: { label: 'Engage - Grow', description: 'Establish firm roots' },
    extend: { label: 'Extend - Neighbor', description: 'Bear fruit and spread seed' }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getVitalSignLabel = (key) => {
    const sign = vitalSigns.find(v => v.key === key);
    return sign ? sign.title : key;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        <Navigation />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-lg text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="dashboard-page">
      <IntroVideoPopup />
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Welcome Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-heading font-bold text-[#2B6B7F] mb-2">
            Welcome, {user?.name}
          </h1>
          <p className="text-slate-600 mb-4">Continue your journey to joy-filled wholeness</p>
          
          {/* Daily Scripture Verse */}
          {dailyVerse && (
            <div className="bg-white/70 backdrop-blur-sm border border-[#2B6B7F]/20 rounded-xl p-4 shadow-sm" data-testid="daily-verse">
              <div className="flex items-start gap-3">
                <Quote className="w-5 h-5 text-[#D4A574] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-700 italic leading-relaxed text-sm">
                    "{dailyVerse.text}"
                  </p>
                  <p className="text-[#2B6B7F] font-semibold mt-2 text-xs">
                    — {dailyVerse.reference}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <Card className="mb-8" data-testid="quick-actions">
          <CardHeader>
            <CardTitle className="text-2xl font-heading text-[#2B6B7F]">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              {/* Row 1: Path, Vital Signs, Sermon */}
              <Button
                onClick={() => navigate('/discipleship')}
                variant="outline"
                className="h-20 flex-col gap-1 border-[#D4A574] hover:bg-[#D4A574]/10"
                data-testid="btn-discipleship"
              >
                <TrendingUp className="w-4 h-4 text-[#D4A574]" />
                <span className="text-xs">Path</span>
              </Button>

              <Button
                onClick={() => navigate('/journal')}
                variant="outline"
                className="h-20 flex-col gap-1 border-[#5FA8C1] hover:bg-[#5FA8C1]/10"
                data-testid="btn-journal"
              >
                <Activity className="w-4 h-4 text-[#5FA8C1]" />
                <span className="text-xs text-center leading-tight">Vital Signs<br/>Journal</span>
              </Button>

              <Button
                onClick={() => navigate('/sermon')}
                variant="outline"
                className="h-20 flex-col gap-1 border-[#2B6B7F] hover:bg-[#2B6B7F]/10"
                data-testid="btn-sermon"
              >
                <BookOpen className="w-4 h-4 text-[#2B6B7F]" />
                <span className="text-xs">Sermon</span>
              </Button>

              {/* Row 2: Media, Resources, Tracker */}
              <Button
                onClick={() => navigate('/livestream')}
                variant="outline"
                className="h-20 flex-col gap-1 border-red-500 hover:bg-red-50"
                data-testid="btn-livestream"
              >
                <Youtube className="w-4 h-4 text-red-500" />
                <span className="text-xs">Media</span>
              </Button>

              <Button
                onClick={() => navigate('/resources')}
                variant="outline"
                className="h-20 flex-col gap-1 border-indigo-600 hover:bg-indigo-50"
                data-testid="btn-resources"
              >
                <Book className="w-4 h-4 text-indigo-600" />
                <span className="text-xs">Resources</span>
              </Button>

              <Button
                onClick={() => navigate('/gamification')}
                variant="outline"
                className="h-20 flex-col gap-1 border-orange-500 hover:bg-orange-50"
                data-testid="btn-tracker"
              >
                <Trophy className="w-4 h-4 text-orange-500" />
                <span className="text-xs">Tracker</span>
              </Button>
            </div>

            {/* Featured Prayer Wall Button */}
            <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
              <Button
                onClick={() => navigate('/prayers')}
                className="w-full h-14 bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white shadow-lg hover:shadow-xl transition-all"
                data-testid="btn-prayer"
              >
                <div className="flex items-center justify-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="!w-5 !h-5" />
                  </span>
                  <span className="text-lg font-semibold">Prayer Wall</span>
                </div>
              </Button>

              {/* Featured Daily Devotions Button */}
              <Button
                onClick={() => navigate('/daily-devotionals')}
                className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg hover:shadow-xl transition-all"
                data-testid="btn-daily-devotion"
              >
                <div className="flex items-center justify-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Sun className="!w-5 !h-5" />
                  </span>
                  <span className="text-lg font-semibold">Daily Devotions</span>
                </div>
              </Button>

              {/* Quiet entry point to any private groups the user is a member of.
                  Renders nothing if the user has none. */}
              <DashboardGroupsCard />
            </div>

            {/* Share App Button */}
            <div className="mt-4 flex justify-center">
              <Button
                onClick={() => setShowShareDialog(true)}
                variant="outline"
                className="border-[#2B6B7F] text-[#2B6B7F] hover:bg-[#2B6B7F]/10"
                data-testid="btn-share-app"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share This App
              </Button>
            </div>

            {/* Visit WFMC Website Button */}
            <div className="mt-3 flex justify-center">
              <a
                href="https://www.westlandfmc.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="outline"
                  className="border-[#2B6B7F] text-[#2B6B7F] hover:bg-[#2B6B7F]/10"
                  data-testid="btn-visit-wfmc"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Visit WFMC Website
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* App Tutorials CTA — quick access to the walk-through videos */}
        <Card
          className="mb-8 overflow-hidden border-2 border-teal-200 bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate('/tutorials')}
          data-testid="tutorials-cta-card"
        >
          <CardContent className="p-5">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#2B6B7F] to-teal-500 flex items-center justify-center">
                <PlayCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-teal-700 mb-1">
                  New to the app?
                </p>
                <h3 className="text-xl md:text-2xl font-heading font-bold text-slate-800 mb-1">
                  Watch App Tutorials
                </h3>
                <p className="text-sm text-slate-600">
                  Short walk-throughs of every part of the app — start with the 4-minute Intro Movie
                  or jump straight to the section you want to learn.
                </p>
              </div>
              <div className="flex-shrink-0 w-full md:w-auto">
                <Button
                  className="bg-[#2B6B7F] hover:bg-[#245565] w-full md:w-auto"
                  onClick={(e) => { e.stopPropagation(); navigate('/tutorials'); }}
                  data-testid="tutorials-cta-btn"
                >
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Watch Tutorials
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alpha Film Series CTA — placed under Quick Actions */}
        <Card className="mb-8 overflow-hidden border-2 border-red-200 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50" data-testid="alpha-cta-card">
          <CardContent className="p-5">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-red-700 mb-1">
                  Exploring Christianity
                </p>
                <h3 className="text-xl md:text-2xl font-heading font-bold text-slate-800 mb-1">
                  Alpha Film Series
                </h3>
                <p className="text-sm text-slate-600">
                  14 episodes that walk through the basics of the Christian faith — perfect for seekers, new believers, or anyone wanting a refresh.
                </p>
              </div>
              <div className="flex-shrink-0 w-full md:w-auto">
                <AlphaVideoModal
                  buttonLabel="Start at Episode 1"
                  fullWidth
                  testId="dashboard-start-alpha"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Share App Dialog */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
                <Share2 className="w-6 h-6" />
                Share This App
              </DialogTitle>
              <DialogDescription>
                Help others join the Discipleship Journey by sharing this app
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_c6c45f8d-756d-427c-8ad6-2091e406baff/artifacts/xh7hsqn2_NEW%20APP%20SITE%20%281200%20x%20500%20px%29.png"
                alt="App sharing instructions with QR code"
                className="w-full rounded-lg border border-slate-200"
                data-testid="share-qr-image"
              />
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 text-center">
                  Scan the QR code or share this link with friends and family to invite them to the Discipleship Journey app.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="card-hover" data-testid="stats-journals">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Journal Entries</p>
                  <p className="text-3xl font-bold text-[#2B6B7F]">{stats?.total_journals || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-[#2B6B7F]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stats-checkins">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Weekly Check-Ins</p>
                  <p className="text-3xl font-bold text-[#5FA8C1]">{stats?.total_checkins || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#5FA8C1]/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#5FA8C1]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stats-stage">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Current Stage</p>
                  <p className="text-xl font-semibold text-[#7BA888] capitalize">
                    {stageInfo[stats?.current_stage]?.label || 'Explore'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#7BA888]/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#7BA888]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vital Signs Progress */}
        <Card className="mb-8" data-testid="vital-signs-progress">
          <CardHeader>
            <CardTitle className="text-2xl font-heading text-[#2B6B7F]">Your Vital Signs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {vitalSigns.map((sign) => {
                const Icon = sign.icon;
                const count = stats?.vital_sign_counts?.[sign.key] || 0;
                const rating = stats?.average_ratings?.[`${sign.key}_rating`] || 0;
                
                return (
                  <div key={sign.key} className="space-y-3" data-testid={`vital-sign-${sign.key}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${sign.bgColor} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" style={{ color: sign.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-slate-800">{sign.title}</h3>
                          <span className="text-sm text-slate-600">{count} entries</span>
                        </div>
                        <p className="text-xs text-slate-500">{sign.description}</p>
                      </div>
                    </div>
                    {rating > 0 && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-600">Avg Rating (Last 4 weeks)</span>
                          <span className="font-medium" style={{ color: sign.color }}>
                            {rating.toFixed(1)}/10
                          </span>
                        </div>
                        <Progress value={rating * 10} className="h-2" style={{ 
                          '--progress-background': sign.color 
                        }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <div className="grid md:grid-cols-1 gap-6">
          {/* Recent Entries */}
          <Card data-testid="recent-entries">
            <CardHeader>
              <CardTitle className="text-xl font-heading text-[#2B6B7F]">Recent Journal Entries</CardTitle>
            </CardHeader>
            <CardContent>
              {recentEntries.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No journal entries yet. Start writing today!</p>
              ) : (
                <div className="space-y-3">
                  {recentEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className="p-3 rounded-lg bg-slate-50 border border-slate-200"
                      data-testid={`recent-entry-${entry.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-[#2B6B7F]">
                          {getVitalSignLabel(entry.vital_sign)}
                        </span>
                        <span className="text-xs text-slate-500">
                          {formatDate(entry.date)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {entry.entry_text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
